import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  Easing,
} from 'remotion';

const PRIMARY = '#10B981';
const SECONDARY = '#F59E0B';
const ACCENT = '#EF4444';
const BG = '#0F1419';

const plateSections = [
  { label: '谷物', en: 'Grains', angle: 72, color: '#F59E0B', start: 0 },
  { label: '蔬菜', en: 'Vegetables', angle: 72, color: '#10B981', start: 72 },
  { label: '水果', en: 'Fruits', angle: 54, color: '#EF4444', start: 144 },
  { label: '蛋白质', en: 'Protein', angle: 72, color: '#8B5CF6', start: 198 },
  { label: '乳制品', en: 'Dairy', angle: 90, color: '#06B6D4', start: 270 },
];

const macros = [
  { key: '蛋白质', pct: 25, color: '#8B5CF6', kcal: '4 kcal/g' },
  { key: '碳水化合物', pct: 50, color: '#F59E0B', kcal: '4 kcal/g' },
  { key: '脂肪', pct: 25, color: '#EF4444', kcal: '9 kcal/g' },
];

const microGrid = [
  { name: '维生素 A', role: '视力与免疫', color: '#F59E0B' },
  { name: '维生素 C', role: '抗氧化', color: '#10B981' },
  { name: '维生素 D', role: '钙吸收', color: '#06B6D4' },
  { name: 'B 族维生素', role: '能量代谢', color: '#8B5CF6' },
  { name: '钙 Ca', role: '骨骼健康', color: '#E2E8F0' },
  { name: '铁 Fe', role: '携氧', color: '#EF4444' },
  { name: '锌 Zn', role: '伤口愈合', color: '#94A3B8' },
  { name: '钾 K', role: '血压平衡', color: '#22C55E' },
];

const mealExample = [
  { part: '糙米饭', portion: '1 碗', cal: '~200 kcal', color: '#F59E0B' },
  { part: '清蒸鱼', portion: '掌心大', cal: '~150 kcal', color: '#06B6D4' },
  { part: '西兰花', portion: '1 拳', cal: '~50 kcal', color: '#10B981' },
  { part: '豆腐', portion: '半盒', cal: '~90 kcal', color: '#E2E8F0' },
];

const FADE = 12;

const sectionOpacity = (
  frame: number,
  enter: number,
  exit: number,
  fade = FADE,
): number =>
  interpolate(frame, [enter - fade, enter, exit, exit + fade], [0, 1, 1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.inOut(Easing.cubic),
  });

const sectionSlide = (
  frame: number,
  enter: number,
  exit: number,
  fade = FADE,
): number =>
  interpolate(
    frame,
    [enter - fade, enter, exit, exit + fade],
    [36, 0, 0, -24],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

const polarToCartesian = (
  cx: number,
  cy: number,
  radius: number,
  angleDeg: number,
): { x: number; y: number } => {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(rad),
    y: cy + radius * Math.sin(rad),
  };
};

const describeArc = (
  cx: number,
  cy: number,
  innerR: number,
  outerR: number,
  startAngle: number,
  endAngle: number,
): string => {
  const outerStart = polarToCartesian(cx, cy, outerR, startAngle);
  const outerEnd = polarToCartesian(cx, cy, outerR, endAngle);
  const innerEnd = polarToCartesian(cx, cy, innerR, endAngle);
  const innerStart = polarToCartesian(cx, cy, innerR, startAngle);
  const large = endAngle - startAngle <= 180 ? '0' : '1';
  return [
    'M', outerStart.x, outerStart.y,
    'A', outerR, outerR, 0, large, 1, outerEnd.x, outerEnd.y,
    'L', innerEnd.x, innerEnd.y,
    'A', innerR, innerR, 0, large, 0, innerStart.x, innerStart.y,
    'Z',
  ].join(' ');
};

const NutritionPlate: React.FC<{ frame: number; baseFrame: number }> = ({
  frame,
  baseFrame,
}) => {
  const rel = frame - baseFrame;
  const cx = 260;
  const cy = 260;
  const outer = 245;
  const inner = 72;

  return (
    <svg width={520} height={520} viewBox="0 0 520 520">
      <defs>
        <radialGradient id="plateShine" cx="35%" cy="35%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx={cx} cy={cy} r={outer + 10} fill="rgba(255,255,255,0.04)" />
      <circle cx={cx} cy={cy} r={outer} fill="#1e293b" stroke={PRIMARY} strokeWidth={3} />
      {plateSections.map((s, i) => {
        const reveal = spring({
          frame: rel - i * 14,
          fps: 30,
          config: { damping: 12, stiffness: 160 },
        });
        const end = s.start + s.angle;
        const d = describeArc(cx, cy, inner, outer, s.start, end);
        const mid = (s.start + end) / 2;
        const lp = polarToCartesian(cx, cy, (inner + outer) / 2, mid);
        return (
          <g key={s.label} opacity={reveal}>
            <path
              d={d}
              fill={s.color}
              fillOpacity={interpolate(reveal, [0, 1], [0.3, 0.92])}
              stroke="rgba(15,20,25,0.9)"
              strokeWidth={2}
            />
            <text
              x={lp.x} y={lp.y}
              textAnchor="middle" dominantBaseline="middle"
              fill="#fff" fontSize={24} fontWeight={700}
              fontFamily="system-ui, sans-serif"
              style={{ opacity: reveal }}
            >
              {s.label}
            </text>
            <text
              x={lp.x} y={lp.y + 36}
              textAnchor="middle"
              fill="rgba(255,255,255,0.55)" fontSize={22}
              fontFamily="system-ui, sans-serif"
              style={{ opacity: reveal }}
            >
              {s.en}
            </text>
          </g>
        );
      })}
      <circle cx={cx} cy={cy} r={inner - 2} fill={BG} stroke={SECONDARY} strokeWidth={2} />
      <text
        x={cx} y={cy - 8} textAnchor="middle"
        fill="#fff" fontSize={26} fontWeight={700}
        fontFamily="system-ui, sans-serif"
      >
        均衡
      </text>
      <text
        x={cx} y={cy + 26} textAnchor="middle"
        fill="rgba(255,255,255,0.5)" fontSize={22}
        fontFamily="system-ui, sans-serif"
      >
        MyPlate
      </text>
      <circle cx={cx} cy={cy} r={outer} fill="url(#plateShine)" pointerEvents="none" />
    </svg>
  );
};

const MacroBars: React.FC<{ frame: number; baseFrame: number; fps: number }> = ({
  frame,
  baseFrame,
  fps,
}) => {
  const rel = frame - baseFrame;
  return (
    <div style={{ width: '100%', maxWidth: 920 }}>
      {macros.map((m, i) => {
        const s = spring({
          frame: rel - i * 16,
          fps,
          config: { damping: 11, stiffness: 140 },
        });
        const fillW = interpolate(s, [0, 1], [0, m.pct]);
        return (
          <div key={m.key} style={{ marginBottom: 22, opacity: s }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <span style={{ fontSize: 24, fontWeight: 700, color: '#fff' }}>{m.key}</span>
              <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.45)' }}>
                参考占比 {m.pct}% · {m.kcal}
              </span>
            </div>
            <div
              style={{
                height: 36,
                borderRadius: 10,
                background: 'rgba(255,255,255,0.06)',
                overflow: 'hidden',
                border: `1px solid ${m.color}33`,
              }}
            >
              <div
                style={{
                  height: '100%',
                  width: `${fillW}%`,
                  borderRadius: 10,
                  background: `linear-gradient(90deg, ${m.color}, ${m.color}aa)`,
                  boxShadow: `0 0 20px ${m.color}44`,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const CalorieBalance: React.FC<{ frame: number; baseFrame: number; fps: number }> = ({
  frame,
  baseFrame,
  fps,
}) => {
  const rel = frame - baseFrame;
  const intake = interpolate(rel, [0, 90], [20, 72], {
    extrapolateRight: 'clamp',
    easing: Easing.out(Easing.cubic),
  });
  const burn = interpolate(rel, [20, 110], [15, 68], {
    extrapolateRight: 'clamp',
    easing: Easing.out(Easing.cubic),
  });
  const balance = spring({
    frame: rel - 40,
    fps,
    config: { damping: 14, stiffness: 100 },
  });

  return (
    <div
      style={{
        display: 'flex',
        gap: 48,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 28, color: SECONDARY, marginBottom: 10, fontWeight: 600 }}>
          摄入
        </div>
        <div
          style={{
            width: 120,
            height: 200,
            borderRadius: 16,
            background: 'rgba(255,255,255,0.05)',
            border: `2px solid ${SECONDARY}44`,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: `${intake}%`,
              background: `linear-gradient(180deg, ${SECONDARY}, #D97706)`,
              borderRadius: '0 0 12px 12px',
            }}
          />
        </div>
      </div>
      <div
        style={{
          fontSize: 60,
          fontWeight: 800,
          color: 'rgba(255,255,255,0.25)',
          opacity: balance,
          transform: `scale(${interpolate(balance, [0, 1], [0.5, 1])})`,
        }}
      >
        ≈
      </div>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 28, color: PRIMARY, marginBottom: 10, fontWeight: 600 }}>
          消耗
        </div>
        <div
          style={{
            width: 120,
            height: 200,
            borderRadius: 16,
            background: 'rgba(255,255,255,0.05)',
            border: `2px solid ${PRIMARY}44`,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: `${burn}%`,
              background: `linear-gradient(180deg, ${PRIMARY}, #059669)`,
              borderRadius: '0 0 12px 12px',
            }}
          />
        </div>
      </div>
      <div
        style={{
          maxWidth: 570,
          padding: 22,
          borderRadius: 16,
          background: 'rgba(239,68,68,0.08)',
          border: `1px solid ${ACCENT}40`,
          opacity: balance,
        }}
      >
        <div style={{ fontSize: 28, fontWeight: 800, color: ACCENT, marginBottom: 8 }}>
          热量平衡
        </div>
        <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6 }}>
          长期摄入与消耗接近时，体重更稳定；减脂需适度热量缺口，增肌需优质蛋白与训练刺激。
        </div>
      </div>
    </div>
  );
};

export const NutritionGuideAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const opTitle = sectionOpacity(frame, 10, 78, FADE);
  const opPlate = sectionOpacity(frame, 88, 175, FADE);
  const opMacro = sectionOpacity(frame, 185, 282, FADE);
  const opMicro = sectionOpacity(frame, 292, 378, FADE);
  const opMeal = sectionOpacity(frame, 388, 472, FADE);
  const opCalorie = sectionOpacity(frame, 482, 530, FADE);

  const slideTitle = sectionSlide(frame, 10, 78, FADE);
  const slidePlate = sectionSlide(frame, 88, 175, FADE);
  const slideMacro = sectionSlide(frame, 185, 282, FADE);
  const slideMicro = sectionSlide(frame, 292, 378, FADE);
  const slideMeal = sectionSlide(frame, 388, 472, FADE);
  const slideCalorie = sectionSlide(frame, 482, 530, FADE);

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(165deg, ${BG} 0%, #0B1120 50%, #08090c 100%)`,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse 70% 45% at 15% 30%, rgba(16,185,129,0.14), transparent 50%)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse 55% 40% at 90% 70%, rgba(245,158,11,0.12), transparent 48%)',
          pointerEvents: 'none',
        }}
      />

      {/* Section 1: Title */}
      {opTitle > 0 && (
        <AbsoluteFill
          style={{
            opacity: opTitle,
            transform: `translateY(${slideTitle}px)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: 76,
              fontWeight: 800,
              letterSpacing: -1,
              background: `linear-gradient(115deg, ${PRIMARY}, ${SECONDARY})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            营养学基础
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: 30,
              color: 'rgba(255,255,255,0.55)',
              fontWeight: 500,
            }}
          >
            均衡饮食指南 · Macronutrients &amp; Micronutrients
          </p>
        </AbsoluteFill>
      )}

      {/* Section 2: Nutrition Plate */}
      {opPlate > 0 && (
        <AbsoluteFill
          style={{
            opacity: opPlate,
            transform: `translateY(${slidePlate}px)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 48,
            padding: '0 80px',
          }}
        >
          <NutritionPlate frame={frame} baseFrame={92} />
          <div style={{ maxWidth: 1100 }}>
            <h2
              style={{
                margin: '0 0 20px',
                fontSize: 36,
                color: '#fff',
                fontWeight: 700,
                textAlign: 'center',
              }}
            >
              膳食餐盘
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: 28,
                color: 'rgba(255,255,255,0.55)',
                lineHeight: 1.7,
              }}
            >
              餐盘分区逐步呈现：谷物、蔬果、蛋白与乳品合理搭配，可参考膳食指南调整比例。
            </p>
          </div>
        </AbsoluteFill>
      )}

      {/* Section 3: Macronutrients */}
      {opMacro > 0 && (
        <AbsoluteFill
          style={{
            opacity: opMacro,
            transform: `translateY(${slideMacro}px)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 100px',
          }}
        >
          <h2
            style={{
              margin: '0 0 80px',
              fontSize: 38,
              color: '#fff',
              fontWeight: 700,
              textAlign: 'center',
            }}
          >
            三大营养素供能
          </h2>
          <MacroBars frame={frame} baseFrame={190} fps={fps} />
        </AbsoluteFill>
      )}

      {/* Section 4: Micronutrients */}
      {opMicro > 0 && (
        <AbsoluteFill
          style={{
            opacity: opMicro,
            transform: `translateY(${slideMicro}px)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 72px',
          }}
        >
          <h2
            style={{
              margin: '0 0 80px',
              fontSize: 38,
              color: '#fff',
              fontWeight: 700,
              textAlign: 'center',
            }}
          >
            维生素与矿物质
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: 18,
              width: '100%',
              maxWidth: 1200,
            }}
          >
            {microGrid.map((cell, i) => {
              const s = spring({
                frame: frame - 296 - i * 10,
                fps,
                config: { damping: 12, stiffness: 200 },
              });
              return (
                <div
                  key={cell.name}
                  style={{
                    padding: '18px 16px',
                    borderRadius: 14,
                    background: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${cell.color}44`,
                    opacity: s,
                    transform: `translateY(${interpolate(s, [0, 1], [20, 0])}px)`,
                  }}
                >
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      background: cell.color,
                      marginBottom: 10,
                      boxShadow: `0 0 12px ${cell.color}66`,
                    }}
                  />
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#fff' }}>{cell.name}</div>
                  <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.5)', marginTop: 6 }}>
                    {cell.role}
                  </div>
                </div>
              );
            })}
          </div>
        </AbsoluteFill>
      )}

      {/* Section 5: Meal Example */}
      {opMeal > 0 && (
        <AbsoluteFill
          style={{
            opacity: opMeal,
            transform: `translateY(${slideMeal}px)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 72px',
          }}
        >
          <h2
            style={{
              margin: '0 0 80px',
              fontSize: 38,
              color: '#fff',
              fontWeight: 700,
              textAlign: 'center',
            }}
          >
            健康一餐示例
          </h2>
          <div
            style={{
              display: 'flex',
              gap: 32,
              alignItems: 'center',
              width: '100%',
              maxWidth: 1100,
            }}
          >
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
              }}
            >
              {mealExample.map((m, i) => {
                const s = spring({
                  frame: frame - 394 - i * 12,
                  fps,
                  config: { damping: 11, stiffness: 180 },
                });
                return (
                  <div
                    key={m.part}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 18,
                      padding: '16px 20px',
                      borderRadius: 14,
                      background: 'rgba(255,255,255,0.04)',
                      borderLeft: `4px solid ${m.color}`,
                      opacity: s,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 28, fontWeight: 700, color: '#fff' }}>{m.part}</div>
                      <div style={{ fontSize: 26, color: 'rgba(255,255,255,0.45)' }}>
                        {m.portion}
                      </div>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 700, color: m.color }}>{m.cal}</div>
                  </div>
                );
              })}
            </div>
            <svg width={380} height={300} viewBox="0 0 380 300" style={{ flexShrink: 0 }}>
              <defs>
                <linearGradient id="bowlGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={PRIMARY} stopOpacity="0.35" />
                  <stop offset="100%" stopColor={SECONDARY} stopOpacity="0.15" />
                </linearGradient>
              </defs>
              <ellipse
                cx={190} cy={180} rx={150} ry={65}
                fill="url(#bowlGrad)" stroke={PRIMARY} strokeWidth={2}
              />
              <path
                d="M 40 180 Q 190 260 340 180"
                fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth={2}
              />
              {[0, 1, 2, 3].map((i) => {
                const ox = 100 + i * 55;
                const float = Math.sin((frame + i * 20) * 0.08) * 6;
                return (
                  <circle
                    key={i}
                    cx={ox}
                    cy={130 + float + i * 3}
                    r={18 + i * 2}
                    fill={mealExample[i % mealExample.length].color}
                    opacity={0.75}
                  />
                );
              })}
              <text
                x={190} y={36} textAnchor="middle"
                fill="rgba(255,255,255,0.7)" fontSize={28}
                fontFamily="system-ui, sans-serif"
              >
                多样化食材 · 少油少盐
              </text>
            </svg>
          </div>
        </AbsoluteFill>
      )}

      {/* Section 6: Calorie Balance */}
      {opCalorie > 0 && (
        <AbsoluteFill
          style={{
            opacity: opCalorie,
            transform: `translateY(${slideCalorie}px)`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <h2
            style={{
              margin: '0 0 80px',
              fontSize: 38,
              color: '#fff',
              fontWeight: 700,
            }}
          >
            每日热量与平衡
          </h2>
          <CalorieBalance frame={frame} baseFrame={486} fps={fps} />
        </AbsoluteFill>
      )}

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 4,
          background: 'rgba(255,255,255,0.06)',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${(frame / 540) * 100}%`,
            background: `linear-gradient(90deg, ${PRIMARY}, ${SECONDARY})`,
          }}
        />
      </div>
    </AbsoluteFill>
  );
};
